import { createFileRoute } from '@tanstack/react-router';
import { isGameTypeEnabled } from '@/lib/platform-filters';
import { useGetPickemsQuery } from '@/api/pickems/getPickems';
import { useGetSquaresQuery } from '@/api/squares/getSquares';
import { useGetDailyRevealsQuery } from '@/api/daily-reveals/getDailyReveals';
import { useGetAnalyticsQuery, type AnalyticsPeriod } from '@/api/analytics/getAnalytics';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Activity,
  CheckCircle2,
  TrendingUp,
  TrendingDown,
  Clock,
  UsersRound,
  UserPlus,
  BarChart3,
  Gamepad2,
  MapPin,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

export const Route = createFileRoute('/dashboard/')({
  component: DashboardIndex,
});

// ── Types ─────────────────────────────────────────────────────────────────────

type TimePeriod = 1 | 7 | 14 | 30;

const GAME_PERIOD_OPTIONS: { value: TimePeriod; label: string }[] = [
  { value: 1, label: '1d' },
  { value: 7, label: '7d' },
  { value: 14, label: '14d' },
  { value: 30, label: '30d' },
];

const ANALYTICS_PERIOD_OPTIONS: { value: AnalyticsPeriod; label: string }[] = [
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
  { value: 'all', label: 'All Time' },
];

const ACTIVE_STATUSES = new Set(['active', 'inplay', 'live', 'locked', 'open']);
const COMPLETE_STATUSES = new Set(['complete', 'completed', 'final']);
const PENDING_STATUSES = new Set(['pending', 'scheduled', 'draft', 'published']);

function isActiveStatus(s?: string) {
  return ACTIVE_STATUSES.has(s?.toLowerCase() ?? '');
}
function isCompleteStatus(s?: string) {
  return COMPLETE_STATUSES.has(s?.toLowerCase() ?? '');
}
function isPendingStatus(s?: string) {
  return PENDING_STATUSES.has(s?.toLowerCase() ?? '');
}

function gameTypeLabel(gt: string) {
  if (gt === 'pickem') return "Pick'em";
  if (gt === 'squares') return 'Squares';
  if (gt === 'daily_reveal') return 'Daily Reveal';
  return gt.charAt(0).toUpperCase() + gt.slice(1);
}

type ContestType = 'pickem' | 'squares' | 'daily_reveal' | 'squads';
type ContestTypeCounts = Record<ContestType, number>;
const CONTEST_TYPE_ORDER: ContestType[] = ['daily_reveal', 'squares', 'pickem', 'squads'];

function emptyContestTypeCounts(): ContestTypeCounts {
  return {
    daily_reveal: 0,
    squares: 0,
    pickem: 0,
    squads: 0,
  };
}

function countContestTypes(contests: Array<{ type: ContestType }>): ContestTypeCounts {
  return contests.reduce((acc, contest) => {
    acc[contest.type] += 1;
    return acc;
  }, emptyContestTypeCounts());
}

function contestTypeBreakdown(counts: ContestTypeCounts) {
  return (
    <div className="space-y-0.5">
      {CONTEST_TYPE_ORDER.filter(isGameTypeEnabled).map(type => (
        <div key={type} className="flex items-center justify-between gap-2">
          <span>{gameTypeLabel(type)}</span>
          <span className="font-medium tabular-nums text-foreground">
            {counts[type].toLocaleString()}
          </span>
        </div>
      ))}
    </div>
  );
}

function fmtDate(dateStr: string) {
  const d = new Date(dateStr + 'T00:00:00Z');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function fmtCompactNumber(n: number) {
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${+(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${+(n / 1_000).toFixed(1)}k`;
  return String(n);
}

function computePctChange(actual: number, expected: number) {
  if (expected === 0) return null;
  const pct = Math.round(((actual - expected) / expected) * 1000) / 10;
  return { pct, isPositive: pct >= 0 };
}

// Running total of a per-day series. Applied to `new_active_users_per_day`
// (per-day first-active-in-period counts), the cumsum yields cumulative
// distinct active users in the period — each user contributes once on
// their first-active day, so Σ|F_t| = |⋃A_t|.
function cumulative(rows: { date: string; count: number }[]) {
  let acc = 0;
  return rows.map(r => ({ date: r.date, count: (acc += r.count) }));
}

// ── Shared UI pieces ──────────────────────────────────────────────────────────

interface PeriodButtonsProps<T extends string | number> {
  options: { value: T; label: string }[];
  current: T;
  onChange: (v: T) => void;
}
function PeriodButtons<T extends string | number>({
  options,
  current,
  onChange,
}: PeriodButtonsProps<T>) {
  return (
    <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
      {options.map(({ value, label }) => (
        <Button
          key={String(value)}
          variant={current === value ? 'default' : 'ghost'}
          size="sm"
          className="h-7 px-3 text-xs"
          onClick={() => onChange(value)}
        >
          {label}
        </Button>
      ))}
    </div>
  );
}

interface StatCardProps {
  title: string;
  value: string | number;
  description?: React.ReactNode;
  icon: React.ReactNode;
  trend?: { value: string; isPositive: boolean };
  loading?: boolean;
}
function StatCard({ title, value, description, icon, trend, loading }: StatCardProps) {
  if (loading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">{title}</CardTitle>
          {icon}
        </CardHeader>
        <CardContent>
          <Skeleton className="h-8 w-24 mb-2" />
          {description && <Skeleton className="h-4 w-32" />}
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">
          {typeof value === 'number' ? value.toLocaleString() : value}
        </div>
        {description && <div className="text-xs text-muted-foreground mt-1">{description}</div>}
        {trend && (
          <div className="flex items-center mt-2">
            <TrendingUp
              className={`h-4 w-4 mr-1 ${trend.isPositive ? 'text-green-600' : 'text-red-600'}`}
            />
            <span
              className={`text-xs font-medium ${trend.isPositive ? 'text-green-600' : 'text-red-600'}`}
            >
              {trend.value}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Shared averages + comparison row ─────────────────────────────────────────

interface AveragesRowProps {
  avgBreakdown: { daily: number; weekly: number; monthly: number };
  comparison?: { pct: number; isPositive: boolean } | null;
}
function AveragesRow({ avgBreakdown, comparison }: AveragesRowProps) {
  return (
    <div className="flex items-center justify-between flex-wrap gap-2">
      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        <span>
          <span className="font-semibold text-foreground">
            {avgBreakdown.daily.toLocaleString()}
          </span>{' '}
          avg/day
        </span>
        <span>
          <span className="font-semibold text-foreground">
            {avgBreakdown.weekly.toLocaleString()}
          </span>{' '}
          avg/wk
        </span>
        <span>
          <span className="font-semibold text-foreground">
            {avgBreakdown.monthly.toLocaleString()}
          </span>{' '}
          avg/mo
        </span>
      </div>
      {comparison && (
        <div
          className={`flex items-center gap-1 text-xs font-semibold ${comparison.isPositive ? 'text-green-600' : 'text-red-600'}`}
        >
          {comparison.isPositive ? (
            <TrendingUp className="h-3.5 w-3.5" />
          ) : (
            <TrendingDown className="h-3.5 w-3.5" />
          )}
          {comparison.isPositive ? '+' : ''}
          {comparison.pct}% vs avg
        </div>
      )}
    </div>
  );
}

// ── Analytics chart card (bar or line) ────────────────────────────────────────

interface ChartCardProps {
  title: string;
  total: number;
  data: { date: string; count: number }[];
  avgBreakdown?: { daily: number; weekly: number; monthly: number };
  comparison?: { pct: number; isPositive: boolean } | null;
  loading: boolean;
  color?: string;
  chartType?: 'bar' | 'line';
}

function ChartCard({
  title,
  total,
  data,
  avgBreakdown,
  comparison,
  loading,
  color = '#3b82f6',
  chartType = 'bar',
}: ChartCardProps) {
  const chartData = data.map(d => ({ date: fmtDate(d.date), count: d.count }));
  const tooltipStyle = {
    fontSize: 12,
    borderRadius: 6,
    border: '1px solid hsl(var(--border))',
    background: 'hsl(var(--card))',
    color: 'hsl(var(--card-foreground))',
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold">{title}</CardTitle>
          {loading ? (
            <Skeleton className="h-6 w-20" />
          ) : (
            <span className="text-2xl font-bold tabular-nums">{total.toLocaleString()}</span>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-44 w-full" />
        ) : data.length === 0 ? (
          <div className="h-44 flex items-center justify-center text-sm text-muted-foreground">
            No data for this period
          </div>
        ) : chartType === 'line' ? (
          <ResponsiveContainer width="100%" height={176}>
            <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                interval="preserveStartEnd"
              />
              <YAxis
                tick={{ fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                tickFormatter={fmtCompactNumber}
                width={40}
              />
              <Tooltip contentStyle={tooltipStyle} cursor={{ stroke: 'hsl(var(--muted))' }} />
              <Line
                type="monotone"
                dataKey="count"
                stroke={color}
                strokeWidth={2}
                dot={false}
                name={title}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <ResponsiveContainer width="100%" height={176}>
            <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                interval="preserveStartEnd"
              />
              <YAxis
                tick={{ fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                tickFormatter={fmtCompactNumber}
                width={40}
              />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'hsl(var(--muted))' }} />
              <Bar dataKey="count" fill={color} radius={[3, 3, 0, 0]} name={title} />
            </BarChart>
          </ResponsiveContainer>
        )}

        {avgBreakdown && (
          <div className="mt-3 pt-3 border-t">
            <AveragesRow avgBreakdown={avgBreakdown} comparison={comparison} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Analytics metric card (with DAU/WAU/MAU) ─────────────────────────────────

interface MetricCardProps {
  title: string;
  value: number | string;
  unit?: string;
  avgBreakdown?: { daily: number; weekly: number; monthly: number };
  comparison?: { pct: number; isPositive: boolean } | null;
  icon: React.ReactNode;
  loading: boolean;
  valueFormatter?: (v: number) => string;
}

function MetricCard({
  title,
  value,
  unit,
  avgBreakdown,
  comparison,
  icon,
  loading,
  valueFormatter,
}: MetricCardProps) {
  const display =
    typeof value === 'number'
      ? valueFormatter
        ? valueFormatter(value)
        : value.toLocaleString()
      : value;
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        {loading ? (
          <>
            <Skeleton className="h-8 w-24 mb-2" />
            <Skeleton className="h-4 w-40" />
          </>
        ) : (
          <>
            <div className="text-2xl font-bold tabular-nums">
              {display}
              {unit && (
                <span className="text-sm font-normal text-muted-foreground ml-1">{unit}</span>
              )}
            </div>
            {avgBreakdown && (
              <div className="mt-1.5">
                <AveragesRow avgBreakdown={avgBreakdown} comparison={comparison} />
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ── Main dashboard ────────────────────────────────────────────────────────────

function DashboardIndex() {
  const [timePeriod, setTimePeriod] = useState<TimePeriod>(7);
  const [analyticsPeriod, setAnalyticsPeriod] = useState<AnalyticsPeriod>('7d');

  // ── Game metrics data (from existing list APIs) ────────────────────────────
  const { data: pickems = [], isLoading: pickemsLoading } = useGetPickemsQuery();
  const { data: squares = [], isLoading: squaresLoading } = useGetSquaresQuery();
  const { data: dailyReveals = [], isLoading: dailyRevealsLoading } = useGetDailyRevealsQuery();
  const gamesLoading = pickemsLoading || squaresLoading || dailyRevealsLoading;

  // ── Analytics data (from new backend endpoint) ─────────────────────────────
  const { data: analytics, isLoading: analyticsLoading } = useGetAnalyticsQuery(analyticsPeriod);

  // ── Game metrics computation ────────────────────────────────────────────────
  const gameStats = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - timePeriod);
    const inPeriod = (d?: string | null) => !!d && new Date(d) >= cutoff;

    const allContests = [
      ...(pickems as any[]).map(p => ({
        type: 'pickem' as const,
        status: p.status || p.pickem_status,
        start: p.start || p.start_utc || p.start_date,
        entries: p.entry_count ?? 0,
        scored_at: p.scored_at,
        title: p.title_en || p.title_fr,
        id: p.pickem_id || p.id,
      })),
      ...(squares as any[]).map(s => ({
        type: 'squares' as const,
        status: s.status,
        start: s.start_time_utc,
        entries: s.total_entries_to_date ?? 0,
        scored_at: undefined,
        title: s.contest_name_en || s.contest_name_fr,
        id: s.squares_contest_id,
      })),
      ...(dailyReveals as any[]).map(d => ({
        type: 'daily_reveal' as const,
        status: d.status,
        start: d.start || d.start_utc,
        entries: d.entry_count ?? 0,
        scored_at: d.scored_at,
        title: d.title_en || d.title_fr,
        id: d.pickem_id || d.id,
      })),
    ];

    // Daily reveals don't have a meaningful contest-level start date — actual play
    // dates live on daily_reveal_days. Bypass the period filter for them across all
    // tiles so they show up in Active, Completed, and Upcoming counts.
    const inPeriodOrDailyReveal = (c: { type: ContestType; start?: string | null }) =>
      c.type === 'daily_reveal' || inPeriod(c.start);

    const active = allContests.filter(c => isActiveStatus(c.status));
    const completed = allContests.filter(
      c => isCompleteStatus(c.status) && inPeriodOrDailyReveal(c)
    );
    const pending = allContests.filter(c => isPendingStatus(c.status) && inPeriodOrDailyReveal(c));

    const unscoredPickems = (pickems as any[]).filter(
      (p: any) => isCompleteStatus(p.status || p.pickem_status) && !p.scored_at
    );

    return {
      activeGames: active.length,
      completedGames: completed.length,
      pendingGames: pending.length,
      activeByType: countContestTypes(active as Array<{ type: ContestType }>),
      completedByType: countContestTypes(completed as Array<{ type: ContestType }>),
      pendingByType: countContestTypes(pending as Array<{ type: ContestType }>),
      activeUsers: active.reduce((s, c) => s + c.entries, 0),
      totalUsers: [...active, ...completed, ...pending].reduce((s, c) => s + c.entries, 0),
      unscoredPickems,
    };
  }, [pickems, squares, dailyReveals, timePeriod]);

  // ── Analytics comparison values ─────────────────────────────────────────────
  const analyticsComparisons = useMemo(() => {
    if (!analytics) return null;
    const periodDays =
      analyticsPeriod === '7d' ? 7 : analyticsPeriod === '30d' ? 30 : analytics.all_time.total_days;
    return {
      signups: computePctChange(
        analytics.totals.signups,
        analytics.averages.signups.daily * periodDays
      ),
      activeUsers: computePctChange(
        analytics.totals.active_users,
        analytics.averages.active_users.daily * periodDays
      ),
      entries: computePctChange(
        analytics.totals.entries,
        analytics.averages.entries.daily * periodDays
      ),
    };
  }, [analytics, analyticsPeriod]);

  const periodLabel = analyticsPeriod === 'all' ? 'All Time' : analyticsPeriod;

  const cumulativeSignups = useMemo(
    () => cumulative(analytics?.signups_per_day ?? []),
    [analytics?.signups_per_day]
  );
  const cumulativeActiveUsers = useMemo(
    () => cumulative(analytics?.new_active_users_per_day ?? []),
    [analytics?.new_active_users_per_day]
  );

  // Analytics is a shared endpoint — it is registered for every client and reports on every
  // game's tables, which exist in every database whether or not the client deployed the game.
  // Undeployed games therefore come back as real zero rows rather than a failed request, so
  // they have to be filtered out here or the breakdown lists games this client doesn't run.
  const deployedGameBreakdown = useMemo(
    () => (analytics?.game_breakdown ?? []).filter(row => isGameTypeEnabled(row.game_type)),
    [analytics?.game_breakdown]
  );

  return (
    <div className="p-6 space-y-8">
      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold mb-1">Dashboard</h1>
          <p className="text-muted-foreground">Overview of your Pickem application performance</p>
        </div>
        <PeriodButtons
          options={GAME_PERIOD_OPTIONS}
          current={timePeriod}
          onChange={setTimePeriod}
        />
      </div>

      {/* ── Game counts ───────────────────────────────────────────────────── */}
      <div>
        <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-3">
          Contests
        </h2>
        <div className="grid gap-4 md:grid-cols-3">
          <StatCard
            title="Active Contests"
            value={gameStats.activeGames}
            description={contestTypeBreakdown(gameStats.activeByType)}
            icon={<Activity className="h-4 w-4 text-green-500" />}
            loading={gamesLoading}
          />
          <StatCard
            title="Completed Contests"
            value={gameStats.completedGames}
            description={contestTypeBreakdown(gameStats.completedByType)}
            icon={<CheckCircle2 className="h-4 w-4 text-muted-foreground" />}
            loading={gamesLoading}
          />
          <StatCard
            title="Upcoming Contests"
            value={gameStats.pendingGames}
            description={contestTypeBreakdown(gameStats.pendingByType)}
            icon={<Clock className="h-4 w-4 text-yellow-500" />}
            loading={gamesLoading}
          />
        </div>
      </div>

      {/* Unscored pickems alert — hidden, uncomment to re-enable
      {gameStats.unscoredPickems.length > 0 && (...)} */}

      {/* ── Analytics section ─────────────────────────────────────────────── */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">Analytics</h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              All-time totals: {analytics?.all_time.signups.toLocaleString() ?? '—'} sign-ups ·{' '}
              {analytics?.all_time.active_users.toLocaleString() ?? '—'} active users ·{' '}
              {analytics?.all_time.entries.toLocaleString() ?? '—'} entries
            </p>
          </div>
          <PeriodButtons
            options={ANALYTICS_PERIOD_OPTIONS}
            current={analyticsPeriod}
            onChange={setAnalyticsPeriod}
          />
        </div>

        {/* Charts row */}
        <div className="grid gap-4 md:grid-cols-2">
          <ChartCard
            title="Sign Ups"
            total={analytics?.totals.signups ?? 0}
            data={analytics?.signups_per_day ?? []}
            avgBreakdown={analytics?.averages.signups ?? { daily: 0, weekly: 0, monthly: 0 }}
            comparison={analyticsComparisons?.signups ?? null}
            loading={analyticsLoading}
            color="#3b82f6"
          />
          <ChartCard
            title="Active Users"
            total={analytics?.totals.active_users ?? 0}
            data={analytics?.active_users_per_day ?? []}
            avgBreakdown={analytics?.averages.active_users ?? { daily: 0, weekly: 0, monthly: 0 }}
            comparison={analyticsComparisons?.activeUsers ?? null}
            loading={analyticsLoading}
            color="#10b981"
          />
        </div>

        {/* Cumulative line charts row */}
        <div className="grid gap-4 md:grid-cols-2">
          <ChartCard
            title="Cumulative Sign Ups"
            total={cumulativeSignups[cumulativeSignups.length - 1]?.count ?? 0}
            data={cumulativeSignups}
            loading={analyticsLoading}
            color="#3b82f6"
            chartType="line"
          />
          <ChartCard
            title="Cumulative Active Users"
            total={cumulativeActiveUsers[cumulativeActiveUsers.length - 1]?.count ?? 0}
            data={cumulativeActiveUsers}
            loading={analyticsLoading}
            color="#10b981"
            chartType="line"
          />
        </div>

        {/* Metric cards row */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            title="Entries"
            value={analytics?.totals.entries ?? 0}
            avgBreakdown={analytics?.averages.entries}
            comparison={analyticsComparisons?.entries ?? null}
            icon={<BarChart3 className="h-4 w-4 text-muted-foreground" />}
            loading={analyticsLoading}
          />
          <MetricCard
            title="Entries per User"
            value={analytics?.totals.entries_per_user ?? 0}
            icon={<UsersRound className="h-4 w-4 text-muted-foreground" />}
            loading={analyticsLoading}
            valueFormatter={v => v.toFixed(2)}
          />
          <MetricCard
            title="% Played Squares + Daily Reveal"
            value={analytics?.totals.pct_played_all_games ?? 0}
            unit="%"
            icon={<Gamepad2 className="h-4 w-4 text-muted-foreground" />}
            loading={analyticsLoading}
            valueFormatter={v => v.toFixed(1)}
          />
          <MetricCard
            title={`Sign-ups — ${periodLabel}`}
            value={analytics?.totals.signups ?? 0}
            icon={<UserPlus className="h-4 w-4 text-blue-500" />}
            loading={analyticsLoading}
          />
        </div>

        {/* Game breakdown table */}
        <div>
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-widest mb-3 flex items-center gap-2">
            <Gamepad2 className="h-4 w-4" />
            Game Breakdown — {periodLabel}
          </h3>
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Game Type</TableHead>
                  <TableHead className="text-right">Active Users</TableHead>
                  <TableHead className="text-right">% of Users</TableHead>
                  <TableHead className="text-right">Entries</TableHead>
                  <TableHead className="text-right">% of Entries</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {analyticsLoading ? (
                  Array.from({ length: 3 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 5 }).map((__, j) => (
                        <TableCell key={j}>
                          <Skeleton className="h-4 w-full" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : deployedGameBreakdown.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-6">
                      No data
                    </TableCell>
                  </TableRow>
                ) : (
                  deployedGameBreakdown.map(row => (
                    <TableRow key={row.game_type}>
                      <TableCell className="font-medium">{gameTypeLabel(row.game_type)}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {row.active_users.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="inline-flex items-center gap-1">
                          <span className="tabular-nums">{row.active_users_pct}%</span>
                          <div className="h-1.5 w-16 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full bg-blue-500 rounded-full"
                              style={{ width: `${row.active_users_pct}%` }}
                            />
                          </div>
                        </span>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {row.entries.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="inline-flex items-center gap-1">
                          <span className="tabular-nums">{row.entries_pct}%</span>
                          <div className="h-1.5 w-16 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full bg-emerald-500 rounded-full"
                              style={{ width: `${row.entries_pct}%` }}
                            />
                          </div>
                        </span>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </div>

        {/* Location breakdown table */}
        <div>
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-widest mb-3 flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            Users by State / Province (All Time)
          </h3>
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>State / Province</TableHead>
                  <TableHead className="text-right">Users</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {analyticsLoading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 2 }).map((__, j) => (
                        <TableCell key={j}>
                          <Skeleton className="h-4 w-full" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (analytics?.location_breakdown ?? []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2} className="text-center text-muted-foreground py-6">
                      No location data
                    </TableCell>
                  </TableRow>
                ) : (
                  (analytics?.location_breakdown ?? []).map((row, i) => (
                    <TableRow key={`${row.state}-${row.country}-${i}`}>
                      <TableCell className="font-medium">{row.state ?? '—'}</TableCell>
                      <TableCell className="text-right tabular-nums font-semibold">
                        {row.user_count.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </div>
      </div>
    </div>
  );
}
